<template>
    <vx-expandable-card color="info">
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <div>
                    <span class="font-extrabold text-2xl text-slate-700">Lifecycle Status Definitions</span>
                    <p class="text-sm text-slate-500 mb-0">
                        Configure how Intelligent Inbox treats lifecycle status and steers AI replies.
                    </p>
                </div>

                <div v-if="definitions.length" class="flex flex-col gap-2">
                    <div ref="sortableEl" class="flex flex-col gap-2">
                        <div
                            v-for="(definition, index) in definitions"
                            :key="definition.id ?? definition.name + '-' + index"
                            class="lifecycle-status-draggable flex items-center"
                        >
                            <span
                                class="lifecycle-status-handle pr-4 py-4"
                                :class="props.disabled ? 'pointer-events-none opacity-50' : 'cursor-move'"
                            >
                                <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
                            </span>
                            <lifecycle-status-definition-card
                                class="grow"
                                :definition="definition"
                                :disabled="props.disabled"
                                @edit="openEdit(index)"
                                @delete="removeDefinition(index)"
                            />
                        </div>
                    </div>
                </div>

                <div v-if="definitions.length < MAX_DEFINITIONS" class="flex">
                    <vx-box class="p-4">
                        <vx-button
                            data-test="new-definition-btn"
                            size="lg"
                            :disabled="props.disabled"
                            @click="openCreate"
                        >
                            New Definition
                            <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </vx-box>
                </div>

                <vx-alert v-if="definitions.length >= MAX_DEFINITIONS" color="info" :closeable="false">
                    Maximum of {{ MAX_DEFINITIONS }} lifecycle status definitions reached.
                </vx-alert>
            </div>
        </template>
    </vx-expandable-card>

    <modal-lifecycle-status-definition
        v-model:visible="modalVisible"
        :definition="modalDefinition"
        :editIndex="modalEditIndex"
        @saved="onDefinitionSaved"
    />
</template>

<script lang="ts" setup>
import { faCirclePlus, faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBox, VxButton, VxExpandableCard, VxSwal } from '@voxie/frontend-components';
import { useSortable } from '@vueuse/integrations/useSortable';
import { ref } from 'vue';
import type {
    LifecycleStatusDefinitionFormData,
    LifecycleStatusDefinitionType,
} from '~/services/intelligentInboxSettings/intelligentInboxSettingsClient.types';
import LifecycleStatusDefinitionCard from './LifecycleStatusDefinitionCard.vue';
import ModalLifecycleStatusDefinition from './ModalLifecycleStatusDefinition.vue';

const MAX_DEFINITIONS = 20;

const props = defineProps<{
    disabled?: boolean;
}>();

const definitions = defineModel<LifecycleStatusDefinitionType[]>({ required: true });

const modalVisible = ref(false);
const modalDefinition = ref<LifecycleStatusDefinitionType | null>(null);
const modalEditIndex = ref<number | null>(null);

const openCreate = (): void => {
    modalDefinition.value = null;
    modalEditIndex.value = null;
    modalVisible.value = true;
};

const openEdit = (index: number): void => {
    modalDefinition.value = definitions.value[index] ?? null;
    modalEditIndex.value = index;
    modalVisible.value = true;
};

const removeDefinition = (index: number): void => {
    const definition = definitions.value[index];

    VxSwal.confirmation(
        'Are you sure?',
        `Are you sure you want to delete "${definition?.name}"?`,
        'danger',
        async () => {
            definitions.value = definitions.value.filter((_, i) => i !== index);
        }
    );
};

const onDefinitionSaved = (data: LifecycleStatusDefinitionFormData, editIndex: number | null): void => {
    if (null !== editIndex && undefined !== definitions.value[editIndex]) {
        definitions.value = definitions.value.map((definition, i) =>
            i === editIndex ? { ...definition, ...data } : definition
        );
    } else {
        definitions.value = [...definitions.value, { ...data } as LifecycleStatusDefinitionType];
    }
    modalVisible.value = false;
};

const sortableEl = ref<HTMLElement | null>(null);

useSortable(sortableEl, definitions, {
    handle: '.lifecycle-status-handle',
    draggable: '.lifecycle-status-draggable',
    animation: 200,
    onUpdate: (e: { oldIndex: number; newIndex: number }) => {
        const next = [...definitions.value];
        next.splice(e.newIndex, 0, ...next.splice(e.oldIndex, 1));
        definitions.value = next;
    },
} as Record<string, unknown>);
</script>
